import { ChangeEvent, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Avatar,
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Menu,
  MenuItem,
  Skeleton,
  TextField,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import Edit from "@mui/icons-material/Edit";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import heic2any from "heic2any";
import CustomImageViewer from "@app/components/atoms/CustomImageViewer/CustomImageViewer";
import { ImageCropDialog } from "@app/pages/Features/GroupList/GroupDetail/Components/InspectionForm/MediaUpload/ImageCropDialog";
import { Button, DataDialog, FileUploadButton } from "@app/components/atoms";
import { IconUser } from "@app/components/icons";
import { getUser, apiPutUser } from "@app/api";
import {
  compressImageFile,
  handleUploadStore,
  isValidMediaFile,
} from "@app/helpers";
import { countryCodeOptions } from "@app/constants/route.constants";
import { UploadFileType } from "@app/constants/report.constants";
import { validProfileName } from "@app/pages/Signup/signup-profile.helper";

const MAX_IMAGE_SIZE_BYTES = 10485760;
const AVATAR_MAX_DIMENSION = 866;
const AVATAR_JPEG_QUALITY = 0.85;
const EMAIL_MAX_LENGTH = 113;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validProfileEmail = (value: string) =>
  EMAIL_REGEX.test(value.trim()) && value.trim().length <= EMAIL_MAX_LENGTH;

const shrinkLabelSx = {
  "&.MuiInputLabel-shrink": {
    backgroundColor: "#fff",
    paddingInline: "4px",
  },
};

const getFileNameFromUrl = (url: string) => {
  const parsed = url.split("?")[0];
  return parsed.substring(parsed.lastIndexOf("/") + 1) || "profile.jpeg";
};

interface ProfileDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved: (pictureUrl: string) => void;
}

export function ProfileDialog({ open, onClose, onSaved }: ProfileDialogProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [fileError, setFileError] = useState("");

  const [email, setEmail] = useState("");
  const [givenName, setGivenName] = useState("");
  const [surname, setSurname] = useState("");
  const [country, setCountry] = useState("");
  const [searchable, setSearchable] = useState(false);

  const [savedPictureUrl, setSavedPictureUrl] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const [pendingPictureFile, setPendingPictureFile] = useState<File | null>(
    null,
  );
  const [pictureRemoved, setPictureRemoved] = useState(false);
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);

  const [pictureMenuAnchor, setPictureMenuAnchor] =
    useState<HTMLElement | null>(null);
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [cropOpen, setCropOpen] = useState(false);
  const [originalCropImage, setOriginalCropImage] = useState("");
  const [originalCropFileName, setOriginalCropFileName] = useState("");

  const previewUrlRef = useRef<string>("");
  const originalCropUrlRef = useRef<string>("");

  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoading(true);
    setError("");
    setFileError("");
    getUser()
      .then((response) => {
        const data = response?.data?.data;
        if (!active || !data) return;
        setEmail(data.email || "");
        setGivenName(data.givenName || "");
        setSurname(data.surname || "");
        setCountry(data.residenceCountryCode || "");
        setSearchable(!!data.isSearchableByEmail);
        setSavedPictureUrl(data.pictureUrl || "");
      })
      .catch(() => {
        if (active) setError(t("profile.saveError"));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [open, t]);

  useEffect(() => {
    if (open) return;
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = "";
    }
    if (originalCropUrlRef.current) {
      URL.revokeObjectURL(originalCropUrlRef.current);
      originalCropUrlRef.current = "";
    }
    setPreviewUrl("");
    setPendingPictureFile(null);
    setPictureRemoved(false);
    setRemoveConfirmOpen(false);
    setPictureMenuAnchor(null);
    setImagePreviewOpen(false);
    setCropOpen(false);
    setOriginalCropImage("");
    setOriginalCropFileName("");
  }, [open]);

  const displayedPictureUrl = pictureRemoved
    ? ""
    : previewUrl || savedPictureUrl;

  const handlePictureChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    setFileError("");

    if (file.size >= MAX_IMAGE_SIZE_BYTES) {
      setFileError(t("messages.errSizeImage"));
      return;
    }

    const isValidFile = await isValidMediaFile(file);
    if (!isValidFile) {
      setFileError(t("messages.corruptFileType"));
      return;
    }

    const fileType = file.type;
    const fileExtension = file.name.split(".").pop()?.toLowerCase();
    const isHeic = fileType === "image/heic" || fileExtension === "heic";

    try {
      let finalFile = file;
      if (isHeic) {
        const convertedBlob = await heic2any({
          blob: file,
          toType: "image/jpeg",
        });
        finalFile = new File(
          [convertedBlob as Blob],
          file.name.replace(/\.heic$/i, ".jpeg"),
          { type: "image/jpeg" },
        );
      }

      if (originalCropUrlRef.current) {
        URL.revokeObjectURL(originalCropUrlRef.current);
      }
      const newOriginalUrl = URL.createObjectURL(finalFile);
      originalCropUrlRef.current = newOriginalUrl;

      setOriginalCropImage(newOriginalUrl);
      setOriginalCropFileName(finalFile.name);
      setCropOpen(true);
    } catch {
      setFileError(t("machineInfo.errormessage"));
    }
  };

  const handleEditPicture = async () => {
    setPictureMenuAnchor(null);
    if (originalCropImage) {
      setCropOpen(true);
      return;
    }
    if (!displayedPictureUrl) return;
    try {
      const response = await fetch(displayedPictureUrl);
      const blob = await response.blob();
      const fileName = getFileNameFromUrl(displayedPictureUrl);
      const blobFile = new File([blob], fileName, {
        type: blob.type || "image/jpeg",
      });
      const url = URL.createObjectURL(blobFile);
      originalCropUrlRef.current = url;
      setOriginalCropImage(url);
      setOriginalCropFileName(blobFile.name);
      setCropOpen(true);
    } catch {
      setFileError(t("machineInfo.errormessage"));
    }
  };

  const handleCropComplete = async (croppedFile: File) => {
    setCropOpen(false);

    let fileToUse = croppedFile;
    try {
      fileToUse = await compressImageFile(
        croppedFile,
        AVATAR_MAX_DIMENSION,
        AVATAR_JPEG_QUALITY,
      );
    } catch {
      // fall back to the uncropped-but-uncompressed file rather than blocking the pick
    }

    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
    }
    const url = URL.createObjectURL(fileToUse);
    previewUrlRef.current = url;

    setPendingPictureFile(fileToUse);
    setPreviewUrl(url);
    setPictureRemoved(false);
  };

  const handleConfirmRemovePicture = () => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = "";
    }
    if (originalCropUrlRef.current) {
      URL.revokeObjectURL(originalCropUrlRef.current);
      originalCropUrlRef.current = "";
    }
    setPendingPictureFile(null);
    setPreviewUrl("");
    setOriginalCropImage("");
    setOriginalCropFileName("");
    setPictureRemoved(true);
    setRemoveConfirmOpen(false);
  };

  const valid =
    validProfileName(surname) &&
    validProfileName(givenName) &&
    validProfileEmail(email) &&
    !!country;

  const handleSave = async () => {
    if (!valid || saving) return;
    setSaving(true);
    setError("");
    try {
      let pictureUrl: string | undefined;
      if (pendingPictureFile) {
        const uploaded = await handleUploadStore(
          UploadFileType.USER,
          pendingPictureFile,
        );
        if (!uploaded?.filePath) {
          setError(t("profile.saveError"));
          setSaving(false);
          return;
        }
        pictureUrl = uploaded.filePath;
      } else if (pictureRemoved) {
        pictureUrl = "";
      }

      const payload: Record<string, unknown> = {
        givenName: givenName.trim(),
        surname: surname.trim(),
        email: email.trim(),
        residenceCountryCode: country,
        isSearchableByEmail: searchable,
      };
      if (pictureUrl !== undefined) {
        payload.pictureUrl = pictureUrl;
      }

      const response = await apiPutUser(payload);
      if (response?.status !== 200) {
        setError(t("profile.saveError"));
        setSaving(false);
        return;
      }

      // The PUT response re-signs pictureUrl into a fresh, directly-loadable
      // SAS URL server-side — the raw blob path returned by the upload step
      // isn't displayable on its own, so we must use this value, not `pictureUrl`.
      const updatedPictureUrl = response?.data?.data?.pictureUrl || "";
      onSaved(updatedPictureUrl);
      onClose();
    } catch (err: any) {
      setError(
        err?.response?.data?.error?.localizedMessage ||
          err?.response?.data?.error?.message ||
          t("profile.saveError"),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <DataDialog
        disableBackdropClick
        open={open}
        title={<>{t("profile.dialogTitle")}</>}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        dialogActions={
          <div
            className="flex align-items-center justify-content-end"
            style={{ gap: "16px" }}
          >
            <Button
              className="btn-no-border"
              onClick={onClose}
              disabled={saving}
            >
              {t("dialogReport.cancel")}
            </Button>
            <Button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={!valid || saving || loading}
            >
              {saving ? <CircularProgress size={20} /> : t("formAction.save")}
            </Button>
          </div>
        }
      >
        {loading ? (
          <Box sx={{ mt: 0 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
                mb: 3,
              }}
            >
              <Skeleton variant="circular" width={96} height={96} />
              <Skeleton variant="text" width={100} height={24} />
            </Box>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Skeleton
                variant="rounded"
                width="100%"
                height={56}
                sx={{ flex: 1 }}
              />
              <Skeleton
                variant="rounded"
                width="100%"
                height={56}
                sx={{ flex: 1 }}
              />
            </Box>
            <Skeleton
              variant="rounded"
              width="100%"
              height={56}
              sx={{ mt: 2 }}
            />
            <Skeleton variant="text" width="45%" height={24} sx={{ mt: 2 }} />
            <Skeleton
              variant="rounded"
              width="100%"
              height={56}
              sx={{ mt: 2 }}
            />
          </Box>
        ) : (
          <Box sx={{ mt: 0 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
                mb: 3,
              }}
            >
              {displayedPictureUrl ? (
                <Avatar
                  src={displayedPictureUrl}
                  sx={{ width: 96, height: 96, cursor: "pointer" }}
                  onClick={(event) => setPictureMenuAnchor(event.currentTarget)}
                />
              ) : (
                <Box sx={{ width: 96, height: 96 }}>
                  <IconUser size={96} />
                </Box>
              )}
              <Menu
                anchorEl={pictureMenuAnchor}
                open={!!pictureMenuAnchor}
                onClose={() => setPictureMenuAnchor(null)}
              >
                <MenuItem
                  onClick={() => {
                    setPictureMenuAnchor(null);
                    setImagePreviewOpen(true);
                  }}
                >
                  <Visibility sx={{ mr: 1, color: "#4a4a4a" }} />
                  {t("preview")}
                </MenuItem>
                <MenuItem onClick={handleEditPicture}>
                  <Edit sx={{ mr: 1, color: "#4a4a4a" }} />
                  {t("formAction.edit")}
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setPictureMenuAnchor(null);
                    setRemoveConfirmOpen(true);
                  }}
                >
                  <DeleteOutline sx={{ mr: 1, color: "red" }} />
                  {t("machineInfo.removeimage")}
                </MenuItem>
              </Menu>
              <input
                id="profile-picture-upload"
                type="file"
                accept=".png,.jpg,.jpeg,.gif,.heic"
                onChange={handlePictureChange}
                style={{ display: "none" }}
              />
              <FileUploadButton
                htmlFor="profile-picture-upload"
                className="btn-secondary"
              >
                {t("machineInfo.uploadimage")}
              </FileUploadButton>
              {fileError && <Alert severity="error">{fileError}</Alert>}
            </Box>

            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                className="input-field"
                fullWidth
                required
                label={t("profile.lastName")}
                value={surname}
                InputLabelProps={{ sx: shrinkLabelSx }}
                inputProps={{ maxLength: 64 }}
                onChange={(e) => setSurname(e.target.value)}
                error={!!surname && !validProfileName(surname)}
                helperText={
                  surname && !validProfileName(surname)
                    ? t("profile.nameHelperText")
                    : " "
                }
              />
              <TextField
                className="input-field"
                fullWidth
                required
                label={t("profile.firstName")}
                value={givenName}
                InputLabelProps={{ sx: shrinkLabelSx }}
                inputProps={{ maxLength: 64 }}
                onChange={(e) => setGivenName(e.target.value)}
                error={!!givenName && !validProfileName(givenName)}
                helperText={
                  givenName && !validProfileName(givenName)
                    ? t("profile.nameHelperText")
                    : " "
                }
              />
            </Box>

            <TextField
              className="input-field"
              fullWidth
              required
              label={t("profile.email")}
              value={email}
              inputProps={{ maxLength: EMAIL_MAX_LENGTH }}
              onChange={(e) => setEmail(e.target.value)}
              error={!!email && !validProfileEmail(email)}
              helperText={
                email && !validProfileEmail(email)
                  ? t("profile.emailHelperText")
                  : " "
              }
              sx={{ mt: 1 }}
            />

            <FormControlLabel
              className="checkbox-field"
              sx={{ mt: 1 }}
              control={
                <Checkbox
                  checked={searchable}
                  onChange={(e) => setSearchable(e.target.checked)}
                />
              }
              label={t("profile.searchableByEmail")}
            />

            <TextField
              className="input-field select-field"
              fullWidth
              required
              select
              label={t("profile.residence")}
              value={country}
              InputLabelProps={{ sx: shrinkLabelSx }}
              onChange={(e) => setCountry(e.target.value)}
              sx={{ mt: 1 }}
            >
              {countryCodeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </Box>
        )}
      </DataDialog>

      <DataDialog
        disableBackdropClick
        open={removeConfirmOpen}
        title={<>{t("profile.removePictureConfirmTitle")}</>}
        onClose={() => setRemoveConfirmOpen(false)}
        dialogActions={
          <div
            className="flex align-items-center justify-content-end"
            style={{ gap: "16px" }}
          >
            <Button
              className="btn-no-border"
              onClick={() => setRemoveConfirmOpen(false)}
            >
              {t("dialogReport.cancel")}
            </Button>
            <Button
              className="btn btn-primary"
              onClick={handleConfirmRemovePicture}
            >
              {t("clear")}
            </Button>
          </div>
        }
      >
        <p>{t("profile.removePictureConfirm")}</p>
      </DataDialog>

      <ImageCropDialog
        open={cropOpen}
        image={originalCropImage}
        fileName={originalCropFileName || "profile.jpeg"}
        onClose={() => setCropOpen(false)}
        onComplete={handleCropComplete}
      />

      {typeof document !== "undefined" &&
        imagePreviewOpen &&
        displayedPictureUrl &&
        createPortal(
          <CustomImageViewer
            imageUrl={displayedPictureUrl}
            onClose={() => setImagePreviewOpen(false)}
          />,
          document.body,
        )}
    </>
  );
}
